import React from 'react';
import { Check } from 'lucide-react';
import { AppStatusBadge } from '@shared/components/ui/status-badge/AppStatusBadge';
import { CopyButton } from '@shared/components/ui/CopyButton';
import { PANEL_SPACING } from '../../constants/spacing';
import { PANEL_TEXT_STYLES } from '../../constants/text-styles';

interface DetailRowProps {
  label: string;
  value: string;
  subValue?: string;
  isDetailView?: boolean;
  showBadge?: boolean;
  badgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'success';
  isCopyable?: boolean;
  /** Horizontal alignment of the value column. Defaults to 'left'. */
  align?: 'left' | 'right';
}


export const DetailRow = React.memo<DetailRowProps>(({ 
  label, 
  value, 
  isDetailView = false, 
  showBadge = false, 
  badgeVariant = 'secondary',
  isCopyable = false,
  align = 'left'
}) => {
  const valueAlignClassName = align === 'right' ? 'text-right' : 'text-left';
  const renderValue = (text: string) => {
    if (!text.includes('✓')) return text
    
    const cleanedText = text.replace(/✓\s?/g, '')
    return (
      <span className={`inline-flex items-center ${PANEL_SPACING.FLEX_GAP_SMALL}`}>
        <Check className="h-3 w-3 text-success flex-shrink-0" />
        <span>{cleanedText}</span>
      </span>
    )
  }

  const renderContent = () => {
    const content = showBadge ? (
      <AppStatusBadge 
        variant="generic" 
        text={value}
        color={
          badgeVariant === 'destructive' ? 'error' : 
          badgeVariant === 'secondary' ? 'neutral' : 
          badgeVariant === 'success' ? 'success' : 
          'info'
        }
      />
    ) : (
      <span>{renderValue(value)}</span>
    )

    if (!isCopyable) return content

    return (
      <CopyButton
        value={value}
        variant="inline"
        label={value}
        className="min-w-0 max-w-full"
      />
    )
  }

  if (isDetailView) {
    return (
      <div className={`grid grid-cols-[minmax(140px,200px)_1fr] items-start gap-x-6 w-full ${PANEL_SPACING.DETAIL_ROW_PADDING}`}>
        <p className={`${PANEL_TEXT_STYLES.label} normal-case min-w-0`}>
          {label}
        </p>
        <div className={`${PANEL_TEXT_STYLES.value} ${valueAlignClassName} min-w-0`}>
          {renderContent()}
        </div>
      </div>
    )
  }

  return (
    <div className={`grid grid-cols-[minmax(120px,180px)_1fr] items-start gap-x-6 ${PANEL_SPACING.DETAIL_ROW_PADDING}`}>
      <p className={`${PANEL_TEXT_STYLES.label} normal-case min-w-0`}>
        {label}
      </p>
      <div className={`${PANEL_TEXT_STYLES.value} ${valueAlignClassName} min-w-0`}>
        {renderContent()}
      </div>
    </div>
  )
});

DetailRow.displayName = 'DetailRow';
